import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Paper,
    Typography,
    TextField,
} from "@mui/material";
import { useAuth, useNavigation, useLoading, useSnackbar } from "../../Context/Contexts";
import CodeIcon from "@mui/icons-material/Code";
import { ListQuery } from "../../Types/CommonTypes";
import { ShippingServiceJsonViewModel } from "../../Types/ExportTypes";
import CenteredListCard from "../../components/List/CenteredListCardLayout";
import ListCardHeader from "../List/ListCardHeader";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import { useTranslation } from "react-i18next";
import LoadingSection from "../LoadingSection";
import ResponsivePagination from "../ResponsivePagination";

const ShippingServiceList: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const { serviceId = 0 } = params || {};
    const [items, setItems] = useState<ShippingServiceJsonViewModel[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [query, setQuery] = useState<ListQuery>({
        page: 0,
        pageSize: 10,
        filter: "",
        filters: [],
    });
    const [filter, setFilter] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [linesLoading, setLinesLoading] = useState(false);

    const { authService } = useAuth();
    const { navigate } = useNavigation();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const fetchItems = useCallback(async () => {
        setLoading(true);
        setLinesLoading(true);  
        try {
            const response = await authService.post<{ items: ShippingServiceJsonViewModel[]; count: number }>(
                `/settingsApi/GetShippingServiceJsonList?serviceId=${serviceId}`,
                query
            );
            setItems(response.items);
            setTotalCount(response.count);
        } catch (error: any) {
            showSnackbar(`${t("dialog.fetchServiceJsonError")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setLoading(false);
            setLinesLoading(false);
        }
    }, [authService, query, serviceId, showSnackbar]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    //cant when its imported/nestled
    //useEffect(() => {
    //    if (loading) showLoading();
    //    else hideLoading();
    //}, [loading, showLoading, hideLoading]);

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setQuery({ ...query, filter: filter, page: 0 });
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handleSelect = (id?: number | null) => {
        navigate({ path: "/settings/shippingservice/editjson", params: { serviceId: serviceId, id } });
    };

    const handleCreate = () => {
        navigate({ path: "/settings/shippingservice/editjson", params: { serviceId: serviceId } });
    };

    return (
        <CenteredListCard title="Shipping Services Json">
  
                <Box sx={{ marginBottom: 2 }}>
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={handleCreate}
                    >
                        {t("common.create")}
                    </Button>
                </Box>
                <Box
                    component="form"
                    onSubmit={handleSearchSubmit}
                    sx={{
                        display: "flex",
                        gap: 2,
                        marginBottom: 2,
                    }}
                >
                    <TextField
                        label={t("common.search")}
                        variant="outlined"
                        fullWidth
                        onChange={(event) => setFilter(event.target.value)}
                        value={filter}
                        disabled={loading}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={loading}
                    >
                        {t("common.search")}
                    </Button>
                </Box>


                <LoadingSection loading={linesLoading}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {items.map((item) => (
                        <Grid item
                            xs={12}
                            sm={items.length === 1 ? 12 : items.length === 2 ? 6 : 6}
                            md={items.length === 1 ? 12 : items.length === 2 ? 6 : 4}
                            key={item.id}>
                            <Card
                                onClick={() => handleSelect(item.id)}
                                sx={{
                                    cursor: "pointer",
                                    transition: "box-shadow 0.2s ease-in-out",
                                    "&:hover": {
                                        boxShadow: 4,
                                        backgroundColor: "secondary.main",
                                    },
                                }}
                            >
                                <CardContent>
                                    <ListCardHeader
                                        icon={<CodeIcon fontSize="small" color="action" />}
                                        title={item.id}
                                    />

                                    <Typography variant="body2" color="textSecondary">
                                        {t("common.countryCode")}: {item.countryCode}
                                    </Typography>

                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                    {!loading && items.length === 0 && (
                        <Grid item xs={12}>
                            <Typography align="center"> {t("common.noEntriesFound")}</Typography>
                        </Grid>
                    )}
                </Grid>
                </LoadingSection>

                <ResponsivePagination
                    page={query.page}
                    pageSize={query.pageSize}
                    totalCount={totalCount}
                    onPageChange={handlePageChange}
                />
        
        </CenteredListCard>

    );
};

export default ShippingServiceList;